import { useForm } from 'react-hook-form'
import { useEffect, useMemo, useState, useRef, Fragment } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, FileText, Calendar, DollarSign, FileImage, FolderOpen, Download, Trash2, Plus, Edit } from 'lucide-react'
import { ProjetoFormData } from '../tipos/projeto'
import { useQueryClient, useQuery } from 'react-query'
import { projetoService } from '../services/projeto'
import { faturamentoService, OrcamentoCobradoDto } from '../services/faturamento'
import { useGlobalLoading } from '../../../contexts/LoadingContext'
import { useStatusProjeto, usePrioridades, useTiposProjeto, useSolicitantes, useAnalistasSMN, useAnalistasDDP, useDesenvolvedores } from '../hooks/useProjeto'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { ConfirmDialog } from '../../../componentesCompartilhados/ui/ConfirmDialog'
import { Loading } from '../../../componentesCompartilhados/ui/Loading'
import { InputDate, FormField, FileUpload } from '../../../componentesCompartilhados/form'
import { normalizeDateField, normalizarParaBusca } from '../../../utils/formatUtils'
import { useMutationWithToast } from '../../../hooks/useMutationWithToast'
import { zRequiredString, zRequiredDate, zRequiredCode, zRequiredId } from '../../../utils/validacao'
import { useToast } from '../../../contexts/NotificationContext'
import { useUsuario } from '../../../contexts/UsuarioContext'
import { FormularioPessoa } from '../../pessoas/componentes/FormularioPessoa'
import { api } from '../../../servicesGlobais/api'
import { formatCurrency } from '../../../utils'

const normalizarTextoUnico = (valor?: string | null): string => {
  return (valor ?? '').replace(/\s+/g, '').toLowerCase()
}

const dataNaoFutura = (valor: string): boolean => {
  if (!valor)
  {
    return true
  }

  const [ano, mes, dia] = valor.split('-').map(Number)
  if (!ano || !mes || !dia)
  {
    return true
  }

  const dataInformada = new Date(ano, mes - 1, dia)
  const hoje = new Date()
  const hojeSemHora = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())
  return dataInformada <= hojeSemHora
}

type ProjetoValidacao = Pick<ProjetoFormData, 'id' | 'nome' | 'prefClockify'>

const criarProjetoSchema = (projetosExistentes: ProjetoValidacao[], projetoIdEdicao?: number) => z.object({
  numeroTicket: z.preprocess(
    (val) => val === '' || val === null || val === undefined ? '' : String(val),
    zRequiredString
  ),
  nome: zRequiredString,
  descricao: z.string().max(400, 'Descrição deve ter no máximo 400 caracteres').optional(),
  prefClockify: zRequiredString.max(50, 'A quantidade máxima de caracteres do Pref. Clockify é 50'),
  ticketUrl: zRequiredString,
  dataRecebimento: zRequiredDate.refine(dataNaoFutura, {
    message: 'Data de recebimento não pode ser futura.'
  }),
  dataReferenciaValorHora: zRequiredDate.refine(dataNaoFutura, {
    message: 'Data ref. valor hora não pode ser futura.'
  }),
  observacao: z.string().max(4000, 'Observação deve ter no máximo 4000 caracteres').optional(),
  idProjetoClockify: z.string().optional(),
  
  // Relacionamentos
  statusCodigo: zRequiredCode,
  prioridadeCodigo: zRequiredCode,
  tipoProjetoCodigo: zRequiredCode,
  solicitanteId: z.union([
    z.number(),
    z.string().transform(val => {
      if (val === '' || val === undefined || val === null) return undefined
      const num = Number(val)
      return isNaN(num) ? undefined : num
    }),
    z.undefined()
  ]).optional(),
  analistaSMNId: zRequiredId,
  analistaDDPId: zRequiredId,
  desenvolvedorId: z.union([
    z.number(),
    z.string().transform(val => {
      if (val === '' || val === undefined || val === null) return undefined
      const num = Number(val)
      return isNaN(num) ? undefined : num
    }),
    z.undefined()
  ]).optional(),
  
  // Envios e Aprovações
  dataEnvioEstimativa: z.string().optional(),
  dataAprovacaoEstimativa: z.string().optional(),
  dataEnvioEscopo: z.string().optional(),
  dataAprovacaoEscopo: z.string().optional(),
  dataEnvioOrcamento: z.string().optional(),
  dataAprovacaoOrcamento: z.string().optional(),
  
  // Cronograma
  dataEnvio: z.string().optional(),
  dataEstimativaInicio: z.string().optional(),
  dataEstimativaFim: z.string().optional(),
  dataEscopoInicio: z.string().optional(),
  dataEscopoFim: z.string().optional(),
  dataOrcamentoInicio: z.string().optional(),
  dataOrcamentoFim: z.string().optional(),
  dataDesenvolvimentoInicio: z.string().optional(),
  dataDesenvolvimentoFim: z.string().optional(),
  dataTestesInternosInicio: z.string().optional(),
  dataTestesInternosFim: z.string().optional(),
  dataTestesProdutoInicio: z.string().optional(),
  dataTestesProdutoFim: z.string().optional(),
  dataHomologacaoInicio: z.string().optional(),
  dataHomologacaoFim: z.string().optional(),
  dataPublicacaoPreProd: z.string().optional(),
  dataPublicacaoProd: z.string().optional(),
  
  // Orçamento
  escopoOrcado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  escopoGasto: z.any().optional(), // Campo readonly calculado dinamicamente - não validado
  escopoCobrado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  desenvolvimentoOrcado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  desenvolvimentoGasto: z.any().optional(), // Campo readonly calculado dinamicamente - não validado
  desenvolvimentoCobrado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  testesOrcado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  testesGasto: z.any().optional(), // Campo readonly calculado dinamicamente - não validado
  testesCobrado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  homologacaoGasto: z.any().optional(), // Campo readonly calculado dinamicamente - não validado
  homologacaoCobrado: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  horasGastasSemTags: z.any().optional(), // Campo readonly calculado dinamicamente - não validado
  saldoFinal: z.coerce.number({ message: 'Deve ser um número válido.' }).optional(),
  flagEspecial: z.preprocess(val => val === 'true' || val === true, z.boolean()).optional(),
  aplicaEstimativa: z.boolean().optional(),
  aplicaEscopo: z.boolean().optional(),
  aplicaOrcamento: z.boolean().optional(),
}).superRefine((data, ctx) => {
  const nomeNormalizado = normalizarTextoUnico(data.nome)
  const prefNormalizado = normalizarTextoUnico(data.prefClockify)

  if (nomeNormalizado)
  {
    const nomeDuplicado = projetosExistentes.some((projeto) => {
      if (projetoIdEdicao && projeto.id === projetoIdEdicao)
      {
        return false
      }
      return normalizarTextoUnico(projeto.nome) === nomeNormalizado
    })

    if (nomeDuplicado)
    {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nome'],
        message: 'Nome do projeto já cadastrado.'
      })
    }
  }

  if (prefNormalizado)
  {
    const prefixoDuplicado = projetosExistentes.some((projeto) => {
      if (projetoIdEdicao && projeto.id === projetoIdEdicao)
      {
        return false
      }
      return normalizarTextoUnico(projeto.prefClockify || '') === prefNormalizado
    })

    if (prefixoDuplicado)
    {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['prefClockify'],
        message: 'Prefixo já cadastrado.'
      })
    }
  }
})

interface FormularioProjetoProps {
  isOpen: boolean
  onClose: () => void
  projeto?: ProjetoFormData
  isEditing?: boolean
  isLoadingProjeto?: boolean
  renderInline?: boolean // Quando true, renderiza sem Modal (para uso em drawer)
}

// Função auxiliar para obter valores padrão do formulário
function getProjetoFormDefaults(analistaSMNIdDefault?: number): ProjetoFormData {
  return {
    numeroTicket: '',
    nome: '',
    descricao: '',
    prefClockify: '',
    ticketUrl: 'https://momentum.movidesk.com/Ticket/Edit/',
    dataRecebimento: '',
    dataReferenciaValorHora: '',
    idProjetoClockify: '',
    statusCodigo: '',
    prioridadeCodigo: '',
    tipoProjetoCodigo: '',
    solicitanteId: '',
    analistaSMNId: analistaSMNIdDefault || 0,
    analistaDDPId: 0,
    desenvolvedorId: '',
    dataEnvioEstimativa: '',
    dataAprovacaoEstimativa: '',
    dataEnvioEscopo: '',
    dataAprovacaoEscopo: '',
    dataEnvioOrcamento: '',
    dataAprovacaoOrcamento: '',
    dataEnvio: '',
    dataEstimativaInicio: '',
    dataEstimativaFim: '',
    dataEscopoInicio: '',
    dataEscopoFim: '',
    dataOrcamentoInicio: '',
    dataOrcamentoFim: '',
    dataDesenvolvimentoInicio: '',
    dataDesenvolvimentoFim: '',
    dataTestesInternosInicio: '',
    dataTestesInternosFim: '',
    dataTestesProdutoInicio: '',
    dataTestesProdutoFim: '',
    dataHomologacaoInicio: '',
    dataHomologacaoFim: '',
    dataPublicacaoPreProd: '',
    dataPublicacaoProd: '',
    escopoOrcado: '',
    escopoGasto: '',
    escopoCobrado: '',
    desenvolvimentoOrcado: '',
    desenvolvimentoGasto: '',
    desenvolvimentoCobrado: '',
    testesOrcado: '',
    testesGasto: '',
    testesCobrado: '',
    homologacaoGasto: '',
    homologacaoCobrado: '',
    horasGastasSemTags: '',
    saldoFinal: '',
    observacao: '',
    flagEspecial: 'false' as any,
    aplicaEstimativa: true,
    aplicaEscopo: true,
    aplicaOrcamento: true,
    tags: [],
  }
}

// Função auxiliar para normalizar dados do projeto para o formulário
function normalizeProjetoForForm(projeto: ProjetoFormData | undefined): ProjetoFormData {
  if (!projeto) return getProjetoFormDefaults()
  
  // Lista de campos de data
  const dateFields: Array<keyof ProjetoFormData> = [
    'dataRecebimento',
    'dataReferenciaValorHora',
    'dataEnvioEstimativa',
    'dataAprovacaoEstimativa',
    'dataEnvioEscopo',
    'dataAprovacaoEscopo',
    'dataEnvioOrcamento',
    'dataAprovacaoOrcamento',
    'dataEnvio',
    'dataEstimativaInicio',
    'dataEstimativaFim',
    'dataEscopoInicio',
    'dataEscopoFim',
    'dataOrcamentoInicio',
    'dataOrcamentoFim',
    'dataDesenvolvimentoInicio',
    'dataDesenvolvimentoFim',
    'dataTestesInternosInicio',
    'dataTestesInternosFim',
    'dataTestesProdutoInicio',
    'dataTestesProdutoFim',
    'dataHomologacaoInicio',
    'dataHomologacaoFim',
    'dataPublicacaoPreProd',
    'dataPublicacaoProd',
  ]
  
  // Normalizar campos de data - apenas normalizar se o valor for inválido
  // Se o valor já estiver no formato YYYY-MM-DD, preservá-lo
  const normalizedDates = dateFields.reduce((acc, field) => {
    const value = projeto[field] as string | undefined | null
    
    // Se o valor já está no formato YYYY-MM-DD (10 caracteres), preservá-lo
    if (value && typeof value === 'string' && value.trim().length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
      acc[field] = value.trim() as any
    }
    // Caso contrário, normalizar (pode retornar string vazia se inválido)
    else {
      acc[field] = normalizeDateField(value) as any
    }
    
    return acc
  }, {} as Partial<ProjetoFormData>)
  
  // Campos básicos com valores padrão
  const baseFields: Partial<ProjetoFormData> = {
    numeroTicket: projeto.numeroTicket || '',
    nome: projeto.nome || '',
    prefClockify: projeto.prefClockify || '',
    ticketUrl: projeto.ticketUrl || 'https://momentum.movidesk.com/Ticket/Edit/',
    idProjetoClockify: projeto.idProjetoClockify || '',
    statusCodigo: projeto.statusCodigo != null ? String(projeto.statusCodigo) : '',
    prioridadeCodigo: projeto.prioridadeCodigo != null ? String(projeto.prioridadeCodigo) : '',
    tipoProjetoCodigo: projeto.tipoProjetoCodigo != null ? String(projeto.tipoProjetoCodigo) : '',
    solicitanteId: projeto.solicitanteId ? String(projeto.solicitanteId) : '',
    analistaSMNId: projeto.analistaSMNId || 0,
    analistaDDPId: projeto.analistaDDPId || 0,
    desenvolvedorId: projeto.desenvolvedorId ? String(projeto.desenvolvedorId) : '',
    // Campos de orçamento: converter 0 ou nulo para string vazia
    escopoOrcado: projeto.escopoOrcado && projeto.escopoOrcado !== 0 ? String(projeto.escopoOrcado) : '',
    escopoGasto: projeto.escopoGasto && projeto.escopoGasto !== 0 ? String(projeto.escopoGasto) : '',
    escopoCobrado: projeto.escopoCobrado && projeto.escopoCobrado !== 0 ? String(projeto.escopoCobrado) : '',
    desenvolvimentoOrcado: projeto.desenvolvimentoOrcado && projeto.desenvolvimentoOrcado !== 0 ? String(projeto.desenvolvimentoOrcado) : '',
    desenvolvimentoGasto: projeto.desenvolvimentoGasto && projeto.desenvolvimentoGasto !== 0 ? String(projeto.desenvolvimentoGasto) : '',
    desenvolvimentoCobrado: projeto.desenvolvimentoCobrado && projeto.desenvolvimentoCobrado !== 0 ? String(projeto.desenvolvimentoCobrado) : '',
    testesOrcado: projeto.testesOrcado && projeto.testesOrcado !== 0 ? String(projeto.testesOrcado) : '',
    testesGasto: projeto.testesGasto && projeto.testesGasto !== 0 ? String(projeto.testesGasto) : '',
    testesCobrado: projeto.testesCobrado && projeto.testesCobrado !== 0 ? String(projeto.testesCobrado) : '',
    homologacaoGasto: projeto.homologacaoGasto && projeto.homologacaoGasto !== 0 ? String(projeto.homologacaoGasto) : '',
    homologacaoCobrado: projeto.homologacaoCobrado && projeto.homologacaoCobrado !== 0 ? String(projeto.homologacaoCobrado) : '',
    horasGastasSemTags: projeto.horasGastasSemTags && projeto.horasGastasSemTags !== 0 ? String(projeto.horasGastasSemTags) : '',
    saldoFinal: projeto.saldoFinal && projeto.saldoFinal !== 0 ? String(projeto.saldoFinal) : '',
    flagEspecial: String(projeto.flagEspecial ?? false) as any,
    aplicaEstimativa: projeto.aplicaEstimativa ?? true,
    aplicaEscopo: projeto.aplicaEscopo ?? true,
    aplicaOrcamento: projeto.aplicaOrcamento ?? true,
    tags: projeto.tags || [],
  }
  
  // Retornar o projeto com campos normalizados
  // Importante: os campos normalizados sobrescrevem apenas se necessário
  return {
    ...projeto,
    ...normalizedDates,
    ...baseFields,
  }
}

// Schema para validação de documentos
const documentoSchema = z.object({
  nomeArquivo: zRequiredString,
  tipoDocumentoCodigo: z.preprocess(
    (val) => {
      if (val === '' || val === undefined || val === null) return ''
      if (typeof val === 'number') return String(val)
      return String(val)
    },
    z.string().min(1, 'Este campo é obrigatório.')
  ),
  arquivo: z.custom<File | null>()
    .optional()
    .refine(
      (file) => !file || file.size <= 5 * 1024 * 1024,
      'O arquivo deve ter no máximo 5 MB.'
    )
})

type DocumentoFormData = z.infer<typeof documentoSchema>

// Componente para aba de Documentos
function DocumentosTab({ projetoId, dataEnvio }: { projetoId?: number, dataEnvio?: string }) {
  const queryClient = useQueryClient()
  const { startLoading, stopLoading } = useGlobalLoading()
  const [showForm, setShowForm] = useState(false)
  const [editingDocumento, setEditingDocumento] = useState<any | null>(null)
  const [showConfirmExcluir, setShowConfirmExcluir] = useState(false)
  const [documentoParaExcluir, setDocumentoParaExcluir] = useState<number | null>(null)
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<DocumentoFormData & { arquivo?: File | null }>({
    resolver: zodResolver(documentoSchema),
    mode: 'onSubmit',
    defaultValues: {
      nomeArquivo: '',
      tipoDocumentoCodigo: '',
      arquivo: null
    }
  })

  const selectedFile = watch('arquivo')

  const { data: documentos = [], isLoading } = useQuery(
    ['documentos', projetoId],
    () => projetoService.buscarDocumentos(projetoId!),
    {
      enabled: !!projetoId,
      staleTime: 30000
    }
  )

  const { data: tiposDocumento = [], isLoading: isLoadingTipos } = useQuery(
    ['tiposDocumento'],
    () => projetoService.buscarTiposDocumento(),
    {
      staleTime: 300000 // Cache por 5 minutos
    }
  )

  const toast = useToast()

  const deleteMutation = useMutationWithToast({
    mutationFn: (idDocumento: number) => projetoService.excluirDocumento(idDocumento),
    successMessage: 'Documento excluído com sucesso!',
    errorMessage: 'Erro ao excluir documento',
    invalidateQueries: ['documentos'],
    onSuccess: () => {
      queryClient.invalidateQueries(['documentos', projetoId])
    },
  })

  const uploadMutation = useMutationWithToast({
    mutationFn: (data: DocumentoFormData & { arquivo?: File | null }) => {
      if (!projetoId) {
        throw new Error('ID do projeto não encontrado')
      }
      if (editingDocumento) {
        return projetoService.atualizarDocumento(
          editingDocumento.idDocumento,
          projetoId,
          Number(data.tipoDocumentoCodigo),
          data.arquivo || undefined,
          data.nomeArquivo
        )
      }
      if (!data.arquivo) {
        throw new Error('Arquivo é obrigatório')
      }
      return projetoService.uploadDocumento(projetoId, Number(data.tipoDocumentoCodigo), data.arquivo, data.nomeArquivo)
    },
    successMessage: editingDocumento ? 'Documento atualizado com sucesso!' : 'Documento enviado com sucesso!',
    errorMessage: editingDocumento ? 'Erro ao atualizar documento' : 'Erro ao enviar documento',
    invalidateQueries: ['documentos'],
    onSuccess: () => {
      queryClient.invalidateQueries(['documentos', projetoId])
      setShowForm(false)
      setEditingDocumento(null)
      reset({
        nomeArquivo: '',
        tipoDocumentoCodigo: '',
        arquivo: null
      })
    },
  })

  const onSubmit = (data: DocumentoFormData & { arquivo?: File | null }) => {
    if (!editingDocumento && !data.arquivo) {
      toast.error('Erro', 'Arquivo é obrigatório')
      return
    }
    uploadMutation.mutate(data)
  }

  const handleFormSubmit = () => {
    handleSubmit(onSubmit)()
  }

  const handleEdit = (doc: any) => {
    setEditingDocumento(doc)
    reset({
      nomeArquivo: doc.nomeArquivo || doc.tipoDocumentoNome || '',
      tipoDocumentoCodigo: String(doc.tipoDocumentoCodigo),
      arquivo: null
    })
    setShowForm(true)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingDocumento(null)
    reset({
      nomeArquivo: '',
      tipoDocumentoCodigo: '',
      arquivo: null
    })
  }

  const handleDownload = async (idDocumento: number, nomeArquivo: string) => {
    startLoading('Baixando documento...')
    try {
      const blob = await projetoService.downloadDocumento(idDocumento)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nomeArquivo
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Sucesso', 'Documento baixado com sucesso!')
    } catch (error: any) {
      toast.error('Erro', error?.message || 'Erro ao baixar documento')
    } finally {
      stopLoading()
    }
  }

  const handleExcluir = (idDocumento: number) => {
    setDocumentoParaExcluir(idDocumento)
    setShowConfirmExcluir(true)
  }

  const confirmarExcluirDocumento = () => {
    if (documentoParaExcluir !== null) {
      deleteMutation.mutate(documentoParaExcluir)
      setShowConfirmExcluir(false)
      setDocumentoParaExcluir(null)
    }
  }

  // Verificar se usuário é administrador (simplificado - deveria vir do backend)
  const isAdmin = false // TODO: Implementar verificação de papel

  const podeExcluir = (dataEnvio?: string) => {
    if (!dataEnvio) return true // Pode excluir se não foi enviado
    return isAdmin // Só admin pode excluir após envio
  }

  if (isLoading) {
    return <div className="text-center py-8">Carregando documentos...</div>
  }

  const ICON_SIZES = {
    small: 'w-4 h-4',
    medium: 'w-5 h-5',
    large: 'w-6 h-6',
  }

  return (
    <>
      <div className={`bg-white rounded-xl ${showForm ? '' : 'border border-gray-200 shadow-theme-sm'}`}>
        {!showForm && (
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-success-500 to-brand-500 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
                <FileImage className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Documentos</h3>
                <p className="text-sm text-gray-600">{documentos.length} documento(s)</p>
              </div>
            </div>
          </div>
        )}

        {showForm ? (
          <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <FormField
                label={
                  <>
                    Nome do Documento <span className="text-red-500">*</span>
                  </>
                }
                error={errors.nomeArquivo}
                labelClassName="block text-sm font-medium text-gray-700 mb-1"
              >
                <input
                  type="text"
                  {...register('nomeArquivo')}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  disabled={uploadMutation.isLoading}
                  placeholder="Digite o nome do documento"
                />
              </FormField>
            </div>

            <div>
              <FormField
                label={
                  <>
                    Tipo de Documento <span className="text-red-500">*</span>
                  </>
                }
                error={errors.tipoDocumentoCodigo}
                labelClassName="block text-sm font-medium text-gray-700 mb-1"
              >
                <select
                  {...register('tipoDocumentoCodigo')}
                  className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                  disabled={isLoadingTipos || uploadMutation.isLoading}
                >
                  <option value="">Selecione o tipo</option>
                  {tiposDocumento.map((tipo: any) => (
                    <option key={tipo.codigo} value={tipo.codigo}>
                      {tipo.descricao}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </div>

          <div className="mt-4">
            <FileUpload
              label={!editingDocumento ? "Arquivo *" : "Arquivo"}
              selectedFile={selectedFile || undefined}
              onFileSelect={(file) => {
                setValue('arquivo', file || null, { shouldValidate: true })
              }}
              disabled={uploadMutation.isLoading}
              showPreview={false}
              error={errors.arquivo?.message}
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
              disabled={uploadMutation.isLoading}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleFormSubmit}
              className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium flex items-center space-x-2"
              disabled={uploadMutation.isLoading}
            >
              <Save className="w-4 h-4" />
              <span>{editingDocumento ? 'Atualizar' : 'Salvar'}</span>
            </button>
          </div>
        </div>
      ) : documentos.length === 0 ? (
        <div className="p-8 text-center">
          <FileImage className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-semibold text-gray-900 mb-2">Nenhum documento encontrado</h4>
          <p className="text-gray-500">Os documentos gerados aparecerão aqui.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Documento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data de Cadastro
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  AÇÕES
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {documentos.map((doc: any) => (
                <tr key={doc.idDocumento} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
                        <FileImage className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{doc.tipoDocumentoNome || doc.nomeArquivo}</h4>
                        <p className="text-sm text-gray-600 truncate max-w-xs">
                          {doc.extensao ? `.${doc.extensao}` : ''}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">
                    {new Date(doc.dataCadastro).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleDownload(doc.idDocumento, doc.nomeArquivo)}
                        className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                        title="Download"
                      >
                        <Download className={ICON_SIZES.small} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleEdit(doc)}
                        className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                        title="Editar"
                        disabled={uploadMutation.isLoading}
                      >
                        <Edit className={ICON_SIZES.small} />
                      </button>
                      {podeExcluir(dataEnvio) && (
                        <button
                          type="button"
                          onClick={() => handleExcluir(doc.idDocumento)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          title="Excluir"
                          disabled={deleteMutation.isLoading}
                        >
                          <Trash2 className={ICON_SIZES.small} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {!showForm && (
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => {
              reset({
                nomeArquivo: '',
                tipoDocumentoCodigo: '',
                arquivo: null
              })
              setShowForm(true)
            }}
            disabled={!projetoId}
            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 shadow-theme-sm font-medium flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand-500"
          >
            <Plus className="w-4 h-4" />
            <span>Inserir Documento</span>
          </button>
        </div>
      )}

      {/* Confirm Dialog para exclusão de documento */}
      {documentoParaExcluir !== null && (
        <ConfirmDialog
          isOpen={showConfirmExcluir}
          onClose={() => {
            setShowConfirmExcluir(false)
            setDocumentoParaExcluir(null)
          }}
          onConfirm={confirmarExcluirDocumento}
          title="Confirmar Exclusão"
          message="Tem certeza que deseja excluir este documento?"
          confirmText="Excluir"
          cancelText="Cancelar"
          variant="danger"
        />
      )}
    </>
  )
}

export function FormularioProjeto({ isOpen, onClose, projeto, isEditing = false, isLoadingProjeto = false, renderInline = false }: FormularioProjetoProps) {
  const { startLoading, stopLoading } = useGlobalLoading()
  const queryClient = useQueryClient()
  const toast = useToast()
  const { usuario } = useUsuario()
  const isAdmin = usuario?.papeis?.includes('1') ?? false
  const isAnalista = !isAdmin && (usuario?.papeis?.includes('2') ?? false)
  const pessoaId = usuario?.pessoaId
  const [activeTab, setActiveTab] = useState('basicas')
  const [showModalObservacoes, setShowModalObservacoes] = useState(false)
  const [showConfirmDataEnvioCronograma, setShowConfirmDataEnvioCronograma] = useState(false)
  const [dadosPendentesSalvar, setDadosPendentesSalvar] = useState<ProjetoFormData | null>(null)
  const observacoesDefault = {
    observacaoTesteProduto: 'Prazo previsto, pois, depende da disponibilidade dos Analistas do DDP.',
    observacaoHomologacao: 'Prazo previsto, pois, depende da disponibilidade dos usuários e conclusão dos testes de produto.',
    observacaoPublicacao: 'Verificar a disponibilidade do DDP.'
  }
  const [observacoes, setObservacoes] = useState(observacoesDefault)
  const [valoresCobrados, setValoresCobrados] = useState<OrcamentoCobradoDto | null>(null)
  
  // Configuração das abas
  const tabs = [
    { id: 'basicas', label: 'Informações Básicas', icon: FileText },
    { id: 'envios', label: 'Envios e Aprovações', icon: FileText },
    { id: 'cronograma', label: 'Cronograma', icon: Calendar },
    { id: 'orcamento', label: 'Orçamento', icon: DollarSign },
    { id: 'documentos', label: 'Documentos', icon: FileImage },
    { id: 'outras', label: 'Outras Informações', icon: FileText }
  ]

  // Hooks para buscar dados
  const { data: statusProjetos, isLoading: isLoadingStatus } = useStatusProjeto()
  const { data: prioridades, isLoading: isLoadingPrioridades } = usePrioridades()
  const { data: tiposProjeto, isLoading: isLoadingTipos } = useTiposProjeto()
  const { data: solicitantes = [], isLoading: isLoadingSolicitantes, refetch: refetchSolicitantes } = useSolicitantes()
  const { data: analistasSMN, isLoading: isLoadingAnalistasSMN } = useAnalistasSMN()
  const { data: analistasDDP, isLoading: isLoadingAnalistasDDP } = useAnalistasDDP()
  const { data: desenvolvedores, isLoading: isLoadingDesenvolvedores } = useDesenvolvedores()
  const { data: valoresHora = [] } = useQuery(
    ['valorHora'],
    () => api.get<any[]>('/valorhora'),
    {
      staleTime: 60000,
    }
  )

  // Verificar se está carregando dados
  const isDataLoading = isLoadingStatus || isLoadingPrioridades || isLoadingTipos || 
                       isLoadingSolicitantes || isLoadingAnalistasSMN || isLoadingAnalistasDDP || 
                       isLoadingDesenvolvedores

  const { data: projetosValidacao } = useQuery(
    ['projetos-validacao-unico'],
    () => projetoService.buscarProjetos({}),
    {
      enabled: isOpen,
      staleTime: 30000
    }
  )

  const projetoIdEdicao = projeto?.id ? Number(projeto.id) : undefined
  const projetosParaValidacao = (projetosValidacao?.data || []) as ProjetoValidacao[]
  const projetoSchema = useMemo(
    () => criarProjetoSchema(projetosParaValidacao, projetoIdEdicao),
    [projetosParaValidacao, projetoIdEdicao]
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch
  } = useForm<ProjetoFormData>({
    resolver: zodResolver(projetoSchema),
    mode: 'onSubmit',
    defaultValues: getProjetoFormDefaults(isAnalista && pessoaId ? pessoaId : undefined),
  })

  const numeroTicket = watch('numeroTicket')
  const dataReferenciaValorHoraAtual = watch('dataReferenciaValorHora')
  const tipoProjetoCodigoAtual = watch('tipoProjetoCodigo')
  const flagEspecialAtual = watch('flagEspecial')
  const aplicaEstimativaAtual = watch('aplicaEstimativa') ?? true
  const aplicaEscopoAtual = watch('aplicaEscopo') ?? true
  const aplicaOrcamentoAtual = watch('aplicaOrcamento') ?? true
  const solicitanteIdAtual = watch('solicitanteId')
  const descricaoAtual = watch('descricao') || ''
  const observacaoAtual = watch('observacao') || ''
  const possuiFaturamentoAprovado = !!(projeto as any)?.possuiFaturamentoAprovado

  const [isSolicitanteDropdownOpen, setIsSolicitanteDropdownOpen] = useState(false)
  const [solicitanteSearch, setSolicitanteSearch] = useState('')
  const [isSolicitanteModalOpen, setIsSolicitanteModalOpen] = useState(false)

  const solicitanteSelecionado = useMemo(() => {
    if (!solicitanteIdAtual) return null
    const idAtualNumber = typeof solicitanteIdAtual === 'string'
      ? Number(solicitanteIdAtual)
      : solicitanteIdAtual
    if (!idAtualNumber) return null

    return (solicitantes || []).find((pessoa: any) => {
      const idPessoa = pessoa.id || pessoa.idPessoa || pessoa.IdPessoa || 0
      return Number(idPessoa) === Number(idAtualNumber)
    }) || null
  }, [solicitantes, solicitanteIdAtual])

  const descricaoLength = descricaoAtual.length
  const observacaoLength = observacaoAtual.length

  const valorHoraAtual = useMemo(() => {
    if (!Array.isArray(valoresHora) || valoresHora.length === 0) return null
    if (!dataReferenciaValorHoraAtual) return null

    const tipoCodigo = Number(tipoProjetoCodigoAtual || 0)
    if (!tipoCodigo) return null

    const dataRef = new Date(dataReferenciaValorHoraAtual)
    if (Number.isNaN(dataRef.getTime())) return null

    const refTime = dataRef.getTime()

    const candidatos = (valoresHora as any[]).filter((v) => {
      const tipo = Number(v.tipoProjetoCodigo ?? v.TipoProjetoCodigo ?? 0)
      if (tipo !== tipoCodigo) return false

      const inicioStr = v.dataInicioValidade ?? v.DataInicioValidade
      if (!inicioStr) return false
      const inicio = new Date(inicioStr)
      if (Number.isNaN(inicio.getTime())) return false

      const fimStr = v.dataFimValidade ?? v.DataFimValidade
      const fim = fimStr ? new Date(fimStr) : null

      if (inicio.getTime() > refTime) return false
      if (fim && fim.getTime() < refTime) return false

      return true
    })

    if (!candidatos.length) return null

    candidatos.sort((a, b) => {
      const aData = new Date(a.dataInicioValidade ?? a.DataInicioValidade)
      const bData = new Date(b.dataInicioValidade ?? b.DataInicioValidade)
      return bData.getTime() - aData.getTime()
    })

    const escolhido = candidatos[0]
    const valorBase = Number(escolhido.valor ?? escolhido.Valor ?? 0)
    const valorEspecialBruto = escolhido.valorEspecial ?? escolhido.ValorEspecial
    const usarEspecial = (flagEspecialAtual as any) === 'true' || flagEspecialAtual === true

    if (usarEspecial && valorEspecialBruto != null) {
      const especial = Number(valorEspecialBruto)
      if (!Number.isNaN(especial)) return especial
    }

    return Number.isNaN(valorBase) ? null : valorBase
  }, [valoresHora, dataReferenciaValorHoraAtual, tipoProjetoCodigoAtual, flagEspecialAtual])

  useEffect(() => {
    if (solicitanteSelecionado) {
      const p = solicitanteSelecionado as { nome?: string; Nome?: string }
      const nome = p.nome || p.Nome || ''
      setSolicitanteSearch(nome)
      return
    }
    setSolicitanteSearch('')
  }, [solicitanteSelecionado])

  const solicitantesFiltrados = useMemo(() => {
    const termoNorm = normalizarParaBusca(solicitanteSearch)
    if (!termoNorm)
    {
      return solicitantes || []
    }

    return (solicitantes || []).filter((pessoa: any) => {
      const nome = (pessoa.nome || pessoa.Nome || '').toString()
      return normalizarParaBusca(nome).includes(termoNorm)
    })
  }, [solicitantes, solicitanteSearch])

  const handleSelectSolicitante = (pessoa: any) => {
    const idPessoa = pessoa.id || pessoa.idPessoa || pessoa.IdPessoa || 0
    const nome = pessoa.nome || pessoa.Nome || ''

    setValue('solicitanteId', String(idPessoa), {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true,
    })
    setSolicitanteSearch(nome)
    setIsSolicitanteDropdownOpen(false)
  }

  const handleOpenSolicitanteModal = () => {
    setIsSolicitanteModalOpen(true)
    setIsSolicitanteDropdownOpen(false)
  }

  const handleCloseSolicitanteModal = async () => {
    setIsSolicitanteModalOpen(false)
    await refetchSolicitantes()
  }

  useEffect(() => {
    if (numeroTicket && numeroTicket.trim() !== '') {
      const ticketUrlBase = 'https://momentum.movidesk.com/Ticket/Edit/'
      const ticketNumber = numeroTicket.replace(/[^0-9]/g, '').trim()
      if (ticketNumber !== numeroTicket) {
        setValue('numeroTicket', ticketNumber, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false
        })
      }
      setValue('ticketUrl', `${ticketUrlBase}${ticketNumber}`, {
        shouldValidate: false,
        shouldDirty: false,
        shouldTouch: false
      })
    }
  }, [numeroTicket, setValue])

  // Função auxiliar para obter o ID do projeto
  const getProjetoId = (proj: ProjetoFormData | undefined): string | number | null => {
    if (!proj) return null
    // O adapter já normaliza o ID para 'id' (minúsculo)
    return proj.id || null
  }

  // Ref para rastrear o último ID do projeto que foi carregado no formulário
  const lastProjetoId = useRef<string | number | null>(null)
  
  // Ref para rastrear se já resetamos o formulário após o carregamento
  // Isso evita múltiplos resets quando o objeto projeto muda de referência
  const resetAfterLoad = useRef<boolean>(false)

  // Effect para garantir que analista SMN seja setado quando usuário for analista
  useEffect(() => {
    if (isOpen && isAnalista && pessoaId) {
      setValue('analistaSMNId', pessoaId, { shouldValidate: false })
    }
  }, [isOpen, isAnalista, pessoaId, setValue])

  // Effect para resetar o formulário quando o projeto ou modal mudar
  useEffect(() => {
    // Quando o modal fecha, limpar formulário e refs
    if (!isOpen) {
      reset(getProjetoFormDefaults(isAnalista && pessoaId ? pessoaId : undefined))
      lastProjetoId.current = null
      resetAfterLoad.current = false
      return
    }

    // Se está criando novo projeto, resetar para valores padrão
    if (!isEditing) {
      reset(getProjetoFormDefaults(isAnalista && pessoaId ? pessoaId : undefined))
      lastProjetoId.current = null
      resetAfterLoad.current = false
      // Se for analista, garantir que o analistaSMNId esteja setado
      if (isAnalista && pessoaId) {
        setValue('analistaSMNId', pessoaId, { shouldValidate: false })
      }
      return
    }

    // Se está editando, aguardar os dados completos carregarem
    if (isEditing) {
      // Se ainda está carregando, marcar que ainda não resetamos
      if (isLoadingProjeto) {
        resetAfterLoad.current = false
        return
      }

      // Se não tem projeto, não fazer nada
      if (!projeto) {
        return
      }

      // Obter ID do projeto atual
      const projetoIdAtual = getProjetoId(projeto)
      
      // Se o ID do projeto mudou, resetar refs (novo projeto sendo editado)
      if (projetoIdAtual && projetoIdAtual !== lastProjetoId.current) {
        lastProjetoId.current = projetoIdAtual
        resetAfterLoad.current = false
      }

      // Normalizar dados do projeto
      const normalizedProjeto = normalizeProjetoForForm(projeto)
      
      // Verificar se o projeto tem dados válidos
      const hasValidData = normalizedProjeto.nome || normalizedProjeto.statusCodigo || normalizedProjeto.tipoProjetoCodigo
      
      if (!hasValidData) {
        return
      }

      // Resetar o formulário apenas uma vez após o carregamento completo
      // Isso garante que as datas sejam carregadas quando os dados completos chegarem
      if (!resetAfterLoad.current) {
        // Se for analista, forçar o analistaSMNId para o pessoaId do usuário
        if (isAnalista && pessoaId) {
          normalizedProjeto.analistaSMNId = pessoaId
        }
        // Usar reset para atualizar todos os campos de uma vez
        reset(normalizedProjeto, {
          keepDefaultValues: false,
          keepValues: false,
          keepDirty: false,
          keepIsSubmitted: false,
          keepTouched: false,
          keepIsValid: false,
          keepSubmitCount: false,
        })
        resetAfterLoad.current = true
        
        // Após o reset, usar setValue em um requestAnimationFrame para garantir que todos os campos sejam atualizados
        // Isso é necessário porque alguns inputs podem não estar renderizados (em abas não visíveis)
        // e o reset só atualiza inputs que existem no DOM no momento do reset
        // Usar requestAnimationFrame garante que os valores sejam aplicados após o reset ser processado
        requestAnimationFrame(() => {
          // Lista de todos os campos de data para atualizar explicitamente
          // Os outros campos são atualizados pelo reset, mas as datas precisam de atenção especial
          const dateFields: Array<keyof ProjetoFormData> = [
            'dataRecebimento',
            'dataEnvioEstimativa',
            'dataAprovacaoEstimativa',
            'dataEnvioEscopo',
            'dataAprovacaoEscopo',
            'dataEnvioOrcamento',
            'dataAprovacaoOrcamento',
            'dataEstimativaInicio',
            'dataEstimativaFim',
            'dataEscopoInicio',
            'dataEscopoFim',
            'dataOrcamentoInicio',
            'dataOrcamentoFim',
            'dataDesenvolvimentoInicio',
            'dataDesenvolvimentoFim',
            'dataTestesInternosInicio',
            'dataTestesInternosFim',
            'dataTestesProdutoInicio',
            'dataTestesProdutoFim',
            'dataHomologacaoInicio',
            'dataHomologacaoFim',
            'dataPublicacaoPreProd',
            'dataPublicacaoProd',
          ]
          
          // Atualizar todos os campos de data usando setValue
          // Isso garante que os valores sejam atualizados no estado do formulário
          // mesmo se os inputs não estiverem renderizados no momento
          dateFields.forEach((field) => {
            const value = normalizedProjeto[field]
            if (value !== undefined && value !== null && value !== '') {
              setValue(field, value as any, { 
                shouldValidate: false, 
                shouldDirty: false, 
                shouldTouch: false 
              })
            }
          })
        })
      }
    }
  }, [isOpen, isEditing, isLoadingProjeto, projeto, reset, setValue])

  // Effect para buscar valores cobrados quando projeto for carregado
  useEffect(() => {
    const buscarValoresCobrados = async () => {
      if (projeto?.id && isOpen) {
        try {
          const valores = await faturamentoService.obterOrcamentoCobrado(projeto.id)
          setValoresCobrados(valores)
          // Atualizar campos do formulário
          setValue('escopoCobrado', valores.escopoCobrado > 0 ? valores.escopoCobrado.toFixed(2) : '', { shouldValidate: false, shouldDirty: false })
          setValue('desenvolvimentoCobrado', valores.desenvolvimentoCobrado > 0 ? valores.desenvolvimentoCobrado.toFixed(2) : '', { shouldValidate: false, shouldDirty: false })
          setValue('testesCobrado', valores.testesCobrado > 0 ? valores.testesCobrado.toFixed(2) : '', { shouldValidate: false, shouldDirty: false })
          setValue('homologacaoCobrado', valores.homologacaoCobrado > 0 ? valores.homologacaoCobrado.toFixed(2) : '', { shouldValidate: false, shouldDirty: false })
        } catch (error) {
          setValoresCobrados(null)
        }
      } else {
        setValoresCobrados(null)
      }
    }
    buscarValoresCobrados()
  }, [projeto?.id, isOpen, setValue])
  
  // Effect para garantir que quando a aba muda, os valores do formulário sejam sincronizados com os inputs
  // Isso é importante porque quando uma aba é renderizada pela primeira vez, os inputs precisam receber os valores
  useEffect(() => {
    if (isEditing && !isLoadingProjeto && projeto && resetAfterLoad.current) {
      // Quando a aba ativa muda, garantir que os valores do formulário sejam aplicados aos inputs
      // O React Hook Form deve fazer isso automaticamente, mas vamos forçar uma atualização
      // apenas para garantir que os inputs recebam os valores corretos
      const normalizedProjeto = normalizeProjetoForForm(projeto)
      
      // Lista de campos de data para verificar e atualizar se necessário
      const dateFields: Array<keyof ProjetoFormData> = [
        'dataRecebimento',
        'dataEnvioEstimativa',
        'dataAprovacaoEstimativa',
        'dataEnvioEscopo',
        'dataAprovacaoEscopo',
        'dataEnvioOrcamento',
        'dataAprovacaoOrcamento',
        'dataEstimativaInicio',
        'dataEstimativaFim',
        'dataEscopoInicio',
        'dataEscopoFim',
        'dataOrcamentoInicio',
        'dataOrcamentoFim',
        'dataDesenvolvimentoInicio',
        'dataDesenvolvimentoFim',
        'dataTestesInternosInicio',
        'dataTestesInternosFim',
        'dataTestesProdutoInicio',
        'dataTestesProdutoFim',
        'dataHomologacaoInicio',
        'dataHomologacaoFim',
        'dataPublicacaoPreProd',
        'dataPublicacaoProd',
      ]
      
      // Aguardar um frame para garantir que os inputs da aba atual sejam renderizados
      requestAnimationFrame(() => {
        // Atualizar apenas os campos de data usando setValue
        // Isso garante que os valores sejam aplicados aos inputs quando a aba for renderizada
        dateFields.forEach((field) => {
          const value = normalizedProjeto[field]
          if (value !== undefined && value !== null && value !== '') {
            setValue(field, value as any, { 
              shouldValidate: false, 
              shouldDirty: false, 
              shouldTouch: false 
            })
          }
        })
      })
    }
  }, [activeTab, isEditing, isLoadingProjeto, projeto, setValue])


  // Mutations
  const createMutation = useMutationWithToast({
    mutationFn: projetoService.criarProjeto,
    successMessage: 'Projeto criado com sucesso!',
    errorMessage: 'Erro ao criar projeto',
    invalidateQueries: ['projetos', 'projeto'],
    onSuccess: () => onClose(),
  })

  const updateMutation = useMutationWithToast({
    mutationFn: ({ id, data }: { id: string; data: ProjetoFormData }) =>
      projetoService.atualizarProjeto(id, data),
    successMessage: 'Projeto atualizado com sucesso!',
    errorMessage: 'Erro ao atualizar projeto',
    invalidateQueries: ['projetos', 'projeto'],
    onSuccess: async (data) => {
      try {
        const projetoId = getProjetoId(projeto) || data?.id
        if (projetoId) {
          queryClient.invalidateQueries(['projeto', String(projetoId)])
        }
      } catch (error) {
        // Ignorar erros na atualização de verificação
      } finally {
        onClose()
      }
    },
  })

  const handleGerarCronograma = () => {
    setObservacoes(observacoesDefault)
    setShowModalObservacoes(true)
  }

  const handleConfirmarGerarCronograma = async () => {
    if (!projeto?.id) return

    try {
      startLoading()
      await projetoService.gerarCronograma(Number(projeto.id), observacoes)
      setShowModalObservacoes(false)
      setObservacoes(observacoesDefault)
      queryClient.invalidateQueries(['projeto', projeto.id])
      queryClient.invalidateQueries(['documentos', projeto.id])
      toast.success('Sucesso', 'Cronograma gerado com sucesso!')
    } catch (error: any) {
      toast.error('Erro', error?.message || 'Erro ao gerar cronograma')
    } finally {
      stopLoading()
    }
  }

  // Mapeamento de campos para abas
  const campoParaAba: Record<string, string> = {
    // Informações Básicas
    numeroTicket: 'basicas',
    nome: 'basicas',
    prefClockify: 'basicas',
    ticketUrl: 'basicas',
    statusCodigo: 'basicas',
    dataRecebimento: 'basicas',
    prioridadeCodigo: 'basicas',
    tipoProjetoCodigo: 'basicas',
    solicitanteId: 'basicas',
    analistaSMNId: 'basicas',
    analistaDDPId: 'basicas',
    desenvolvedorId: 'basicas',
    idProjetoClockify: 'basicas',
    // Envios e Aprovações
    dataEnvioEstimativa: 'envios',
    dataAprovacaoEstimativa: 'envios',
    dataEnvioEscopo: 'envios',
    dataAprovacaoEscopo: 'envios',
    dataEnvioOrcamento: 'envios',
    dataAprovacaoOrcamento: 'envios',
    // Cronograma
    dataEnvio: 'cronograma',
    dataEstimativaInicio: 'cronograma',
    dataEstimativaFim: 'cronograma',
    dataEscopoInicio: 'cronograma',
    dataEscopoFim: 'cronograma',
    dataOrcamentoInicio: 'cronograma',
    dataOrcamentoFim: 'cronograma',
    dataDesenvolvimentoInicio: 'cronograma',
    dataDesenvolvimentoFim: 'cronograma',
    dataTestesInternosInicio: 'cronograma',
    dataTestesInternosFim: 'cronograma',
    dataTestesProdutoInicio: 'cronograma',
    dataTestesProdutoFim: 'cronograma',
    dataHomologacaoInicio: 'cronograma',
    dataHomologacaoFim: 'cronograma',
    dataPublicacaoPreProd: 'cronograma',
    dataPublicacaoProd: 'cronograma',
    // Orçamento
    escopoOrcado: 'orcamento',
    escopoGasto: 'orcamento',
    escopoCobrado: 'orcamento',
    desenvolvimentoOrcado: 'orcamento',
    desenvolvimentoGasto: 'orcamento',
    desenvolvimentoCobrado: 'orcamento',
    testesOrcado: 'orcamento',
    testesGasto: 'orcamento',
    testesCobrado: 'orcamento',
    homologacaoGasto: 'orcamento',
    homologacaoCobrado: 'orcamento',
    horasGastasSemTags: 'orcamento',
    saldoFinal: 'orcamento',
  }

  // Função para encontrar a aba do primeiro campo com erro
  const encontrarAbaDoPrimeiroErro = (errors: any): string | null => {
    if (!errors) return null
    
    // Percorrer os erros em ordem de prioridade (básicas -> envios -> cronograma -> orçamento)
    const ordemAbas = ['basicas', 'envios', 'cronograma', 'orcamento']
    
    for (const aba of ordemAbas) {
      for (const campo in errors) {
        if (campoParaAba[campo] === aba && errors[campo]) {
          return aba
        }
      }
    }
    
    // Se não encontrou na ordem, retornar a primeira aba com erro
    for (const campo in errors) {
      if (errors[campo] && campoParaAba[campo]) {
        return campoParaAba[campo]
      }
    }
    
    return null
  }

  const isErroDuplicidade = (mensagem?: string) =>
    mensagem === 'Nome do projeto já cadastrado.'
    || mensagem === 'Prefixo já cadastrado.'

  const deveConfirmarDataEnvioCronograma = (data: ProjetoFormData): boolean => {
    const dataEnvioNova = normalizeDateField(data.dataEnvio)
    if (!dataEnvioNova)
      return false

    const dataEnvioAtual = normalizeDateField(projeto?.cronograma?.dataEnvio)
    return dataEnvioNova !== dataEnvioAtual
  }

  const onSubmit = async (data: ProjetoFormData) => {
    try {
      startLoading('Salvando projeto...')
      const projetoId = getProjetoId(projeto) || data?.id
      if (isEditing && projetoId) {
        await updateMutation.mutateAsync({ id: String(projetoId), data })
      } else {
        await createMutation.mutateAsync(data)
      }
    } catch (error: any) {
      // Erros já são tratados nas mutations pelo useMutationWithToast
    } finally {
      stopLoading()
    }
  }

  const onSubmitComConfirmacaoDataEnvio = async (data: ProjetoFormData) => {
    if (deveConfirmarDataEnvioCronograma(data))
    {
      setDadosPendentesSalvar(data)
      setShowConfirmDataEnvioCronograma(true)
      return
    }

    await onSubmit(data)
  }

  // Verificar se o cronograma foi enviado e salvo no banco
  // Desabilitar campos apenas se:
  // 1. Está editando um projeto existente
  // 2. O projeto tem cronograma salvo (tem ID)
  // 3. A data de envio está salva no banco (não apenas preenchida no formulário)
  // 4. Todas as datas do cronograma estão preenchidas e salvas
  const cronogramaSalvo = isEditing && projeto?.cronograma?.id != null
  const dataEnvioSalva = !!projeto?.cronograma?.dataEnvio
  // Etapas não aplicáveis não são exigidas (usa flags salvos do projeto)
  const projAplicaEstimativa = projeto?.aplicaEstimativa ?? true
  const projAplicaEscopo = projeto?.aplicaEscopo ?? true
  const projAplicaOrcamento = projeto?.aplicaOrcamento ?? true
  const todasDatasPreenchidas = cronogramaSalvo &&
    (!projAplicaEstimativa || (!!projeto?.cronograma?.dataEstimativaInicio && !!projeto?.cronograma?.dataEstimativaFim)) &&
    (!projAplicaEscopo || (!!projeto?.cronograma?.dataEscopoInicio && !!projeto?.cronograma?.dataEscopoFim)) &&
    (!projAplicaOrcamento || (!!projeto?.cronograma?.dataOrcamentoInicio && !!projeto?.cronograma?.dataOrcamentoFim)) &&
    !!projeto?.cronograma?.dataDesenvolvimentoInicio &&
    !!projeto?.cronograma?.dataDesenvolvimentoFim &&
    !!projeto?.cronograma?.dataTestesInternosInicio &&
    !!projeto?.cronograma?.dataTestesInternosFim &&
    !!projeto?.cronograma?.dataTestesProdutoInicio &&
    !!projeto?.cronograma?.dataTestesProdutoFim &&
    !!projeto?.cronograma?.dataHomologacaoInicio &&
    !!projeto?.cronograma?.dataHomologacaoFim &&
    !!projeto?.cronograma?.dataPublicacaoPreProd &&
    !!projeto?.cronograma?.dataPublicacaoProd
  
  const camposDesabilitados = cronogramaSalvo && dataEnvioSalva && todasDatasPreenchidas
  const dataEnvioDesabilitada = !todasDatasPreenchidas || dataEnvioSalva

  // Alterna a aplicabilidade de uma etapa (Estimativa/Escopo/Orçamento).
  // Ao desmarcar, limpa as datas dessa etapa nas duas abas (Envios e Cronograma).
  const alternarAplicabilidade = (
    campo: 'aplicaEstimativa' | 'aplicaEscopo' | 'aplicaOrcamento',
    camposParaLimpar: Array<keyof ProjetoFormData>,
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const marcado = e.target.checked
    setValue(campo, marcado, { shouldDirty: true })
    if (!marcado) {
      camposParaLimpar.forEach((c) => setValue(c, '' as any, { shouldDirty: true }))
    }
  }

  const camposEstimativa: Array<keyof ProjetoFormData> = ['dataEnvioEstimativa', 'dataAprovacaoEstimativa', 'dataEstimativaInicio', 'dataEstimativaFim']
  const camposEscopo: Array<keyof ProjetoFormData> = ['dataEnvioEscopo', 'dataAprovacaoEscopo', 'dataEscopoInicio', 'dataEscopoFim']
  const camposOrcamento: Array<keyof ProjetoFormData> = ['dataEnvioOrcamento', 'dataAprovacaoOrcamento', 'dataOrcamentoInicio', 'dataOrcamentoFim']

  // Conteúdo do formulário (reutilizável)
  const formContent = (
    <>
      {isDataLoading || (isEditing && isLoadingProjeto) ? (
        <div className="p-6">
          <div className="flex items-center justify-center py-12">
            <Loading size="lg" text="Carregando dados do projeto..." />
          </div>
        </div>
      ) : (
        <form id="clean-project-form" onSubmit={handleSubmit(onSubmitComConfirmacaoDataEnvio, (errors) => {
          const mensagensDuplicidade = [
            errors.nome?.message,
            errors.prefClockify?.message
          ].filter(Boolean) as string[]

          if (mensagensDuplicidade.length > 0)
          {
            const mensagem = mensagensDuplicidade.join(' ')
            toast.error('Erro', mensagem)
          }

          const abaComErro = encontrarAbaDoPrimeiroErro(errors)
          if (abaComErro && abaComErro !== activeTab) {
            setActiveTab(abaComErro)
            setTimeout(() => {
              const formElement = document.getElementById('clean-project-form')
              if (formElement) {
                formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            }, 100)
          }
        })} className="p-4">
          {/* Navegação das Abas */}
          <div className="mb-3">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-6">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-1.5 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                        activeTab === tab.id
                          ? 'border-brand-500 text-brand-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                    </button>
                  )
                })}
              </nav>
            </div>
          </div>

          {/* Conteúdo das Abas */}
          <div className="max-w-2xl mx-auto min-h-[500px]">
            {/* Aba: Informações Básicas */}
            {activeTab === 'basicas' && (
              <div className="space-y-0">
            <div className="flex items-center space-x-2 mb-2">
                  <FileText className="w-4 h-4 text-brand-500" />
              <h3 className="text-lg font-semibold text-gray-900">Informações Básicas</h3>
            </div>

                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-0">
                  <div className="w-full max-w-[220px]">
                    <FormField
                      label={
                        <>
                          Data ref. valor hora <span className="text-red-500">*</span>
                        </>
                      }
                      error={errors.dataReferenciaValorHora}
                      labelClassName="block text-sm font-medium text-gray-700 mb-0"
                    >
                      <InputDate
                        register={register('dataReferenciaValorHora')}
                        error={!!errors.dataReferenciaValorHora}
                        className="w-full max-w-[200px] py-1 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                      />
                    </FormField>
                  </div>
                  <div className="mt-0 md:mt-0">
                    <p className="text-xs text-gray-500">Valor hora</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {valorHoraAtual != null ? formatCurrency(valorHoraAtual) : '-'}
                    </p>
                  </div>
                </div>

                <div className="space-y-0">
              {/* Número do Ticket e Nome do Projeto na mesma linha */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5">
                <div className="md:col-span-1">
                  <FormField 
                    label={
                      <>
                        Número do Ticket <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.numeroTicket}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <input
                      type="text"
                      {...register('numeroTicket', {
                        onChange: (e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '').slice(0, 6)
                          e.target.value = numericValue
                        }
                      })}
                      maxLength={6}
                      placeholder="Ex: 12345"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
                    />
                  </FormField>
                </div>
                <div className="md:col-span-3">
                  <FormField 
                    label={
                      <>
                        Nome do Projeto <span className="text-red-500">*</span>
                      </>
                    }
                    error={isErroDuplicidade(errors.nome?.message) ? undefined : errors.nome}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <input
                      {...register('nome')}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"                                                                                                                         
                      placeholder="Digite o nome do projeto"
                    />
                  </FormField>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-10 gap-1.5">
                <div className="md:col-span-4">
                  <FormField 
                    label={
                      <>
                        Pref. Clockify <span className="text-red-500">*</span>
                      </>
                    }
                    error={isErroDuplicidade(errors.prefClockify?.message) ? undefined : errors.prefClockify}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <input
                      {...register('prefClockify', {
                        onChange: (e) => setValue('prefClockify', e.target.value.toUpperCase())
                      })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 uppercase"
                      placeholder="Prefixo do projeto no Clockify"
                    />
                  </FormField>
                </div>
                <div className="md:col-span-6">
                  <FormField 
                    label={
                      <>
                        Ticket URL <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.ticketUrl}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <input
                      {...register('ticketUrl')}
                      readOnly
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                      placeholder="URL do ticket"
                    />
                  </FormField>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormField 
                    label={
                      <>
                        Status <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.statusCodigo}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <select
                      {...register('statusCodigo')}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"                                                                                                                         
                    >
                      <option value=""></option>
                      {statusProjetos?.map((status: any) => {
                        const codigo = String(status.codigo ?? status.Codigo ?? '')
                        const descricao = status.descricao || status.Descricao || 'Descrição não encontrada'
                        return (
                          <option key={codigo} value={codigo}>
                            {descricao}
                          </option>
                        )
                      })}
                    </select>
                  </FormField>
                </div>

                <div>
                  <FormField 
                    label={
                      <>
                        Data de Recebimento <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.dataRecebimento}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <InputDate
                      register={register('dataRecebimento')}
                      error={!!errors.dataRecebimento}
                      className="py-1.5 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                    />
                  </FormField>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormField 
                    label={
                      <>
                        Prioridade <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.prioridadeCodigo}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <select
                      {...register('prioridadeCodigo')}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"                                                                                                                         
                    >
                      <option value=""></option>
                      {prioridades?.map((prioridade: any) => {
                        const codigo = String(prioridade.codigo ?? prioridade.Codigo ?? '')
                        const descricao = prioridade.descricao || prioridade.Descricao || 'Descrição não encontrada'
                        return (
                          <option key={codigo} value={codigo}>
                            {descricao}
                          </option>
                        )
                      })}
                    </select>
                  </FormField>
                </div>

                <div className="flex gap-3 items-start">
                  <div className="flex-1">
                    <FormField 
                      label={
                        <>
                          Tipo de Projeto <span className="text-red-500">*</span>
                        </>
                      }
                      error={errors.tipoProjetoCodigo}
                      labelClassName="block text-sm font-medium text-gray-700 mb-1"
                    >
                      <select
                      {...register('tipoProjetoCodigo')}
                      disabled={possuiFaturamentoAprovado}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"                                                                                                                         
                      >
                        <option value=""></option>
                        {tiposProjeto?.map((tipo: any) => {
                          const codigo = String(tipo.codigo ?? tipo.Codigo ?? '')
                          const descricao = tipo.descricao || tipo.Descricao || 'Descrição não encontrada'
                          return (
                            <option key={codigo} value={codigo}>
                              {descricao}
                            </option>
                          )
                        })}
                      </select>
                    </FormField>
                  </div>

                  <div className="flex flex-col shrink-0">
                    <span className="block text-sm font-medium text-gray-700 mb-1">Valor Hora Especial</span>
                    <div className="h-[34px] flex items-center gap-4">
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          {...register('flagEspecial')}
                          value="false"
                          disabled={possuiFaturamentoAprovado}
                          className="h-4 w-4 border-gray-300 text-brand-500 focus:ring-brand-400 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-gray-700">Não</span>
                      </label>
                      <label className="inline-flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="radio"
                          {...register('flagEspecial')}
                          value="true"
                          disabled={possuiFaturamentoAprovado}
                          className="h-4 w-4 border-gray-300 text-brand-500 focus:ring-brand-400 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-gray-700">Sim</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormField 
                    label="Solicitante"
                    error={errors.solicitanteId}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                        placeholder="Selecione ou pesquise o solicitante"
                        value={solicitanteSearch}
                        onChange={(e) => {
                          const valor = e.target.value
                          setSolicitanteSearch(valor)
                          setIsSolicitanteDropdownOpen(true)
                          setValue('solicitanteId', '' as any, {
                            shouldDirty: true,
                            shouldValidate: true,
                            shouldTouch: true,
                          })
                        }}
                        onFocus={() => {
                          setIsSolicitanteDropdownOpen(true)
                        }}
                        onBlur={() => {
                          setTimeout(() => {
                            setIsSolicitanteDropdownOpen(false)
                          }, 150)
                        }}
                      />

                      {isSolicitanteDropdownOpen && (
                        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-theme-sm max-h-60 overflow-auto">
                          <div className="p-2 border-b border-gray-100">
                            <input
                              type="text"
                              className="w-full px-2 py-1 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                              placeholder="Buscar solicitante..."
                              value={solicitanteSearch}
                              onChange={(e) => setSolicitanteSearch(e.target.value)}
                            />
                          </div>

                          {solicitantesFiltrados.length > 0 ? (
                            solicitantesFiltrados.map((pessoa: any) => {
                              const idPessoa = pessoa.id || pessoa.idPessoa || pessoa.IdPessoa || 0
                              const nome = pessoa.nome || pessoa.Nome || 'Nome não encontrado'

                              return (
                                <button
                                  key={idPessoa}
                                  type="button"
                                  className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => handleSelectSolicitante(pessoa)}
                                >
                                  {nome}
                                </button>
                              )
                            })
                          ) : (
                            <div className="p-3 text-sm text-gray-500 flex items-center justify-between">
                              <span>Nenhum solicitante encontrado.</span>
                              <button
                                type="button"
                                className="px-2 py-1 text-xs font-medium text-white bg-brand-500 rounded-md hover:bg-brand-600"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleOpenSolicitanteModal}
                              >
                                Cadastrar
                              </button>
                            </div>
                          )}

                          {solicitantesFiltrados.length > 0 && (
                            <div className="border-t border-gray-100 p-2">
                              <button
                                type="button"
                                className="w-full text-left text-xs text-brand-600 hover:text-brand-700"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={handleOpenSolicitanteModal}
                              >
                                + Cadastrar novo solicitante
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </FormField>
                </div>

                <div>
                  <FormField 
                    label={
                      <>
                        Analista SMN <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.analistaSMNId}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <select
                      {...register('analistaSMNId', { valueAsNumber: true })}
                      disabled={isAnalista}
                      className={`w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 ${isAnalista ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="">Selecione o analista SMN</option>
                      {analistasSMN?.map((pessoa: any) => {
                        const idPessoa = pessoa.id || pessoa.idPessoa || pessoa.IdPessoa || 0
                        const nome = pessoa.nome || pessoa.Nome || 'Nome não encontrado'
                        return (
                          <option key={idPessoa} value={idPessoa}>
                            {nome}
                          </option>
                        )
                      })}
                    </select>
                  </FormField>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FormField 
                    label={
                      <>
                        Analista DDP <span className="text-red-500">*</span>
                      </>
                    }
                    error={errors.analistaDDPId}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <select
                      {...register('analistaDDPId', { valueAsNumber: true })}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"                                                                                                                         
                    >
                      <option value="">Selecione o analista DDP</option>
                      {analistasDDP?.map((pessoa: any) => {
                        const idPessoa = pessoa.id || pessoa.idPessoa || pessoa.IdPessoa || 0
                        const nome = pessoa.nome || pessoa.Nome || 'Nome não encontrado'
                        return (
                          <option key={idPessoa} value={idPessoa}>
                            {nome}
                          </option>
                        )
                      })}
                    </select>
                  </FormField>
                </div>

                <div>
                  <FormField 
                    label="Desenvolvedor"
                    error={errors.desenvolvedorId}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <select
                      {...register('desenvolvedorId')}
                      className="w-full px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"                                                                                                                         
                    >
                      <option value=""></option>
                      {desenvolvedores?.map((pessoa: any) => {
                        const idPessoa = pessoa.id || pessoa.idPessoa || pessoa.IdPessoa || 0
                        const nome = pessoa.nome || pessoa.Nome || 'Nome não encontrado'
                        return (
                          <option key={idPessoa} value={idPessoa}>
                            {nome}
                          </option>
                        )
                      })}
                    </select>
                  </FormField>
                </div>
              </div>

                </div>
              </div>
            )}

            {/* Aba: Envios e Aprovações */}
            {activeTab === 'envios' && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 mb-2">
                  <FileText className="w-4 h-4 text-brand-500" />
                  <h3 className="text-base font-semibold text-gray-900">Envios e Aprovações</h3>
                </div>
                <div className="space-y-1">
                  {/* Tabela de Envios e Aprovações */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="text-left text-sm font-medium text-gray-700 py-1 pr-4"></th>
                          <th className="text-center text-sm font-medium text-gray-700 py-1 px-3">Envio</th>
                          <th className="text-center text-sm font-medium text-gray-700 py-1 px-3">Aprovação</th>
                        </tr>
                      </thead>
                      <tbody className="space-y-1">
                        <tr className="py-1">
                          <td className="py-2 pr-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aplicaEstimativaAtual}
                                onChange={alternarAplicabilidade('aplicaEstimativa', camposEstimativa)}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                              />
                              Estimativa
                            </label>
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEnvioEstimativa')}
                              disabled={!aplicaEstimativaAtual}
                              className="py-2 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataAprovacaoEstimativa')}
                              disabled={!aplicaEstimativaAtual}
                              className="py-2 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                          </td>
                        </tr>
                        <tr className="py-1">
                          <td className="py-2 pr-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aplicaEscopoAtual}
                                onChange={alternarAplicabilidade('aplicaEscopo', camposEscopo)}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                              />
                              Escopo
                            </label>
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEnvioEscopo')}
                              disabled={!aplicaEscopoAtual}
                              className="py-2 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Envio"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataAprovacaoEscopo')}
                              disabled={!aplicaEscopoAtual}
                              className="py-2 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Aprovação"
                            />
                          </td>
                        </tr>
                        <tr className="py-1">
                          <td className="py-2 pr-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aplicaOrcamentoAtual}
                                onChange={alternarAplicabilidade('aplicaOrcamento', camposOrcamento)}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                              />
                              Orçamento
                            </label>
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEnvioOrcamento')}
                              disabled={!aplicaOrcamentoAtual}
                              className="py-2 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Envio"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataAprovacaoOrcamento')}
                              disabled={!aplicaOrcamentoAtual}
                              className="py-2 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Aprovação"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Aba: Cronograma */}
            {activeTab === 'cronograma' && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 mb-2">
                  <Calendar className="w-4 h-4 text-brand-500" />
                  <h3 className="text-base font-semibold text-gray-900">Cronograma</h3>
                </div>
                
                {/* Campo Data de Envio - habilitado somente após geração do cronograma e antes de salvar */}
                <div className="mb-4">
                  <FormField
                    label="Data de Envio"
                    error={errors.dataEnvio}
                    labelClassName="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <InputDate
                      register={register('dataEnvio')}
                      disabled={dataEnvioDesabilitada}
                      className="w-full max-w-[150px] px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Data de Envio"
                    />
                  </FormField>
                </div>

                <div className="space-y-1">
                  {/* Tabela de Cronograma */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="text-left text-sm font-medium text-gray-700 py-2 pr-4"></th>
                          <th className="text-center text-sm font-medium text-gray-700 py-2 px-3">Início</th>
                          <th className="text-center text-sm font-medium text-gray-700 py-2 px-3">Fim</th>
                        </tr>
                      </thead>
                      <tbody className="space-y-2">
                        <tr className="py-3">
                          <td className="py-3 pr-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aplicaEstimativaAtual}
                                onChange={alternarAplicabilidade('aplicaEstimativa', camposEstimativa)}
                                disabled={camposDesabilitados}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 disabled:cursor-not-allowed"
                              />
                              Estimativa
                            </label>
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEstimativaInicio')}
                              disabled={camposDesabilitados || !aplicaEstimativaAtual}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEstimativaFim')}
                              disabled={camposDesabilitados || !aplicaEstimativaAtual}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="py-3 pr-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aplicaEscopoAtual}
                                onChange={alternarAplicabilidade('aplicaEscopo', camposEscopo)}
                                disabled={camposDesabilitados}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 disabled:cursor-not-allowed"
                              />
                              Escopo
                            </label>
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEscopoInicio')}
                              disabled={camposDesabilitados || !aplicaEscopoAtual}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataEscopoFim')}
                              disabled={camposDesabilitados || !aplicaEscopoAtual}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="py-3 pr-4">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={aplicaOrcamentoAtual}
                                onChange={alternarAplicabilidade('aplicaOrcamento', camposOrcamento)}
                                disabled={camposDesabilitados}
                                className="w-4 h-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 disabled:cursor-not-allowed"
                              />
                              Orçamento
                            </label>
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataOrcamentoInicio')}
                              disabled={camposDesabilitados || !aplicaOrcamentoAtual}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataOrcamentoFim')}
                              disabled={camposDesabilitados || !aplicaOrcamentoAtual}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Desenvolvimento</td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataDesenvolvimentoInicio')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataDesenvolvimentoFim')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Testes Internos</td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataTestesInternosInicio')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataTestesInternosFim')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Testes de Produto</td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataTestesProdutoInicio')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataTestesProdutoFim')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Homologação</td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataHomologacaoInicio')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Início"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataHomologacaoFim')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Fim"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Publicação</td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataPublicacaoPreProd')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="PreProd"
                            />
                          </td>
                          <td className="px-3">
                            <InputDate
                              register={register('dataPublicacaoProd')}
                              disabled={camposDesabilitados}
                              className="w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed"
                              placeholder="Prod"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Botão Gerar */}
                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      onClick={handleGerarCronograma}
                      disabled={!(projeto?.cronograma?.podeGerarCronograma ?? false) || !!watch('dataEnvio')}
                      className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Gerar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Aba: Orçamento */}
            {activeTab === 'orcamento' && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 mb-2">
                  <DollarSign className="w-4 h-4 text-brand-500" />
                  <h3 className="text-base font-semibold text-gray-900">Orçamento</h3>
                </div>
                <div className="space-y-1">
                  {/* Campo de Controle - Horas sem Tags */}
                  <div className="mb-4">
                    <div className="flex items-center">
                      <div className="w-32 pr-4">
                        <label className="text-sm font-medium text-gray-700">
                          Horas sem Tags:
                        </label>
                      </div>
                      <div className="px-3">
                        <input
                          type="text"
                          value={watch('horasGastasSemTags') || ''}
                          readOnly
                          className="w-32 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                          placeholder=""
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Tabela de Orçamento */}
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="text-left text-sm font-medium text-gray-700 py-2 pr-4"></th>
                          <th className="text-center text-sm font-medium text-gray-700 py-2 px-3">Orçado</th>
                          <th className="text-center text-sm font-medium text-gray-700 py-2 px-3">Gasto</th>
                          <th className="text-center text-sm font-medium text-gray-700 py-2 px-3">Cobrado</th>
                        </tr>
                      </thead>
                      <tbody className="space-y-2">
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Escopo</td>
                          <td className="px-3">
                          <input
                              type="text"
                              {...register('escopoOrcado', {
                                onChange: (e) => {
                                  const numericValue = e.target.value.replace(/[^0-9.]/g, '').slice(0, 6)
                                  e.target.value = numericValue
                                }
                              })}
                              maxLength={6}
                              placeholder="Horas"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                  <input
                              type="text"
                              value={watch('escopoGasto') || ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                  <input
                              type="text"
                              value={valoresCobrados?.escopoCobrado ? valoresCobrados.escopoCobrado.toFixed(2) : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Desenvolvimento</td>
                          <td className="px-3">
                          <input
                              type="text"
                              {...register('desenvolvimentoOrcado', {
                                onChange: (e) => {
                                  const numericValue = e.target.value.replace(/[^0-9.]/g, '').slice(0, 6)
                                  e.target.value = numericValue
                                }
                              })}
                              maxLength={6}
                              placeholder="Horas"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                  <input
                              type="text"
                              value={watch('desenvolvimentoGasto') || ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                  <input
                              type="text"
                              value={valoresCobrados?.desenvolvimentoCobrado ? valoresCobrados.desenvolvimentoCobrado.toFixed(2) : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Testes</td>
                          <td className="px-3">
                          <input
                              type="text"
                              {...register('testesOrcado', {
                                onChange: (e) => {
                                  const numericValue = e.target.value.replace(/[^0-9.]/g, '').slice(0, 6)
                                  e.target.value = numericValue
                                }
                              })}
                              maxLength={6}
                              placeholder="Horas"
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                  <input
                              type="text"
                              value={watch('testesGasto') || ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                  <input
                              type="text"
                              value={valoresCobrados?.testesCobrado ? valoresCobrados.testesCobrado.toFixed(2) : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                        </tr>
                        <tr className="py-3">
                          <td className="text-sm font-medium text-gray-800 py-3 pr-4">Homologação</td>
                          <td className="px-3">
                            <div className="w-full px-2 py-1"></div>
                          </td>
                          <td className="px-3">
                            <input
                              type="text"
                              value={watch('homologacaoGasto') || ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                          <td className="px-3">
                            <input
                              type="text"
                              value={valoresCobrados?.homologacaoCobrado ? valoresCobrados.homologacaoCobrado.toFixed(2) : ''}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200 text-sm"
                            />
                          </td>
                        </tr>
                      </tbody>
                    </table>
                </div>
              </div>
            </div>
            )}

            {/* Aba: Documentos */}
            {activeTab === 'documentos' && (
              <DocumentosTab projetoId={projeto?.id} dataEnvio={watch('dataEnvio')} />
            )}

            {/* Aba: Outras Informações (última aba) */}
            {activeTab === 'outras' && (
              <div className="space-y-4">
                <div className="flex items-center space-x-2 mb-1">
                  <FileText className="w-4 h-4 text-brand-500" />
                  <h3 className="text-base font-semibold text-gray-900">Outras Informações</h3>
                </div>

                <div className="space-y-4">
                  <FormField
                    label={`Descrição (${descricaoLength}/400)`}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <textarea
                      {...register('descricao')}
                      maxLength={400}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Descrição geral do ticket/projeto (máx. 400 caracteres)"
                    />
                  </FormField>

                  <FormField
                    label={`Observação (${observacaoLength}/4000)`}
                    labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <textarea
                      {...register('observacao')}
                      maxLength={4000}
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Observações adicionais sobre o projeto (máx. 4000 caracteres)"
                    />
                  </FormField>
                </div>
              </div>
            )}
          </div>
        </form>
      )}
    </>
  )

  // Footer do formulário (reutilizável)
  const formFooter = (
    <div className="flex justify-end space-x-3 p-4">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
      >
        Cancelar
      </button>
      <button
        type="submit"
        form="clean-project-form"
        disabled={isSubmitting || createMutation.isLoading || updateMutation.isLoading || isDataLoading || (isEditing && isLoadingProjeto)}
        className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save className="w-4 h-4 mr-2 inline" />
        {isDataLoading || (isEditing && isLoadingProjeto) 
          ? 'Carregando...' 
          : isSubmitting || createMutation.isLoading || updateMutation.isLoading 
            ? 'Salvando...' 
            : isEditing 
              ? 'Atualizar' 
              : 'Salvar'}
      </button>
    </div>
  )

  // Se renderInline, renderizar sem Modal (para uso em drawer)
  if (renderInline) {
    return (
      <Fragment>
        <div className="h-full flex flex-col">
          {formContent}
          <div className="border-t border-gray-200 bg-gray-50 flex-shrink-0">
            {formFooter}
          </div>
        </div>
        <ConfirmDialog
          isOpen={showConfirmDataEnvioCronograma}
          onClose={() => {
            setShowConfirmDataEnvioCronograma(false)
            setDadosPendentesSalvar(null)
          }}
          onConfirm={() => {
            const data = dadosPendentesSalvar
            setShowConfirmDataEnvioCronograma(false)
            setDadosPendentesSalvar(null)
            if (!data)
              return
            onSubmit(data)
          }}
          title="Atenção"
          message="Ao atualizar a data de envio do cronograma, o botão  de geração será bloqueado e você nao conseguira mais editar nem fazer novas gerações sem falar com um administrador. Deseja continuar?"
          confirmText="Continuar"
          cancelText="Cancelar"
          variant="warning"
        />
        <FormularioPessoa
          isOpen={isSolicitanteModalOpen}
          onClose={handleCloseSolicitanteModal}
          pessoa={null}
          isEditing={false}
          cargoFixo="1"
        />
      </Fragment>
    )
  }

  // Renderização normal com Modal
  return (
    <Fragment>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditing ? 'Editar Projeto' : 'Novo Projeto'}
        subtitle="Preencha as informações do projeto"
        size="4xl"
        icon={
          <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <FolderOpen className="w-5 h-5" />
          </div>
        }
        footer={formFooter}
      >
        {formContent}
      </Modal>

      {/* Modal de Observações */}
      <Modal
        isOpen={showModalObservacoes}
        onClose={() => setShowModalObservacoes(false)}
        title="Observações"
      >
        <div className="space-y-4">
          <FormField
            label="Teste de Produto"
            labelClassName="block text-sm font-medium text-gray-700 mb-2"
          >
            <textarea
              value={observacoes.observacaoTesteProduto}
              onChange={(e) => setObservacoes(prev => ({ ...prev, observacaoTesteProduto: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </FormField>

          <FormField
            label="Homologação"
            labelClassName="block text-sm font-medium text-gray-700 mb-2"
          >
            <textarea
              value={observacoes.observacaoHomologacao}
              onChange={(e) => setObservacoes(prev => ({ ...prev, observacaoHomologacao: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </FormField>

          <FormField
            label="Publicação em produção"
            labelClassName="block text-sm font-medium text-gray-700 mb-2"
          >
            <textarea
              value={observacoes.observacaoPublicacao}
              onChange={(e) => setObservacoes(prev => ({ ...prev, observacaoPublicacao: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
            />
          </FormField>

          <div className="flex justify-end space-x-2 mt-6">
            <button
              type="button"
              onClick={() => setShowModalObservacoes(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmarGerarCronograma}
              className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
            >
              Confirmar
            </button>
          </div>
        </div>
      </Modal>
      <ConfirmDialog
        isOpen={showConfirmDataEnvioCronograma}
        onClose={() => {
          setShowConfirmDataEnvioCronograma(false)
          setDadosPendentesSalvar(null)
        }}
        onConfirm={() => {
          const data = dadosPendentesSalvar
          setShowConfirmDataEnvioCronograma(false)
          setDadosPendentesSalvar(null)
          if (!data)
            return
          onSubmit(data)
        }}
        title="Atenção"
        message="Ao atualizar a data de envio do cronograma, o botão  de geração será bloqueado e você nao conseguira mais editar nem fazer novas gerações sem falar com um administrador. Deseja continuar?"
        confirmText="Continuar"
        cancelText="Cancelar"
        variant="warning"
      />
      <FormularioPessoa
        isOpen={isSolicitanteModalOpen}
        onClose={handleCloseSolicitanteModal}
        pessoa={null}
        isEditing={false}
        cargoFixo="1"
      />
    </Fragment>
  )
}